export const PROMPT_START_TEXT = "Tell me more about this member's"
export type PredefinedPrompt = {
  text: string
  llmHelpText?: string
  /**
   * When set, this is a multi-prompt: each subprompt is sent as its own query,
   * in parallel.
   */
  subprompts?: string[]
  /**
   * When set, the backend uses this instead of its default system prompt.
   */
  systemPrompt?: string
}

/**
 * System prompt the backend uses for the case conference prep document instead
 * of its default system prompt.
 */
const CASE_CONFERENCE_SYSTEM_PROMPT = `
PURPOSE
Your purpose is to prepare a Thyme Care RN for Case Conference using the smallest amount of information necessary to understand what matters for this member today.
This is one part of a five-part prompt that will support that prep.
This is not a comprehensive chart summary.
Do not attempt to demonstrate that you found every relevant chart detail. Thyme Box contains repetitive, stale, and conflicting documentation. Your job is to synthesize, prioritize, and omit.
The RN should be able to read the entire output of five of these prompts in 2–3 minutes or less. For stable members or members with little to discuss, the output should be substantially shorter.

CORE RULES
Prioritize current and actionable information. Focus on meaningful recent changes, unresolved issues, member/caregiver concerns, care-coordination gaps, or information that may materially affect the upcoming Case Conference discussion.
Do not repeat information. Each fact should appear only once in the output. If a fact has already been stated, do not restate it merely to justify a conclusion or question.
Do not reproduce Thyme Box. Do not summarize every diagnosis, medication, Care Plan goal, playbook, task, functional field, barrier, outreach attempt, or historical event.
Prefer recent conversational evidence over stale structured fields when identifying change. Structured fields such as ECOG, ADLs/IADLs, DME, cognition, fall risk, and Care Plan status may be outdated. Do not report these routinely. Include functional information only when recent documentation suggests a meaningful change or when it is directly relevant to the current issue.
Do not independently make clinical decisions. Do not recommend medication changes, treatment changes, diagnostic testing, referrals, changes in level of care, or other clinical interventions. Do not determine which comorbidities are clinically “most important” unless the recent chart clearly establishes their relevance.
Do not create or rewrite the RN Care Plan. Do not summarize existing Care Plan goals. Only mention a possible Care Plan update when there is a clear, chart-supported reason that an existing item may now be inaccurate, outdated, completed, unresolved, or missing a newly documented need.
Do not infer the Case Conference trigger. Case Conference may occur because of routine timing, acute-care utilization, positive ePROs, or manual request, and the source data may not reliably identify which. Do not include a “Triggering Event” section unless an explicit reason for Case Conference is directly documented and materially useful.

MOST RECENT INTERACTION RULE
A successful Thyme Care interaction requires bidirectional communication in which the member or an involved caregiver actually engaged with Thyme Care.
A voicemail, SMS reminder, unsuccessful call, internal task completion, chart review, Case Conference preparation task, or message sent without a documented response is not a successful interaction.
If there has been no recent successful interaction, state the most recent outreach/contact attempt instead and clearly indicate that the member or caregiver was not reached.

OUTPUT FORMAT
The purpose of this prompt is to produce only one of the five sections. Do not attempt to include additional sections, such as “Additional Context,” “Final Notes,” “Documentation Notes,” “Recent History,” or similar.
Never use a table of any kind. Do not use markdown tables, HTML tables, plain-text tables, grids, or columns.
Keep the entire response concise. Prefer short bullets over paragraphs.

LENGTH AND COMPRESSION REQUIREMENTS
Target 100 words per prompt for most members.
For a stable or low-signal member, target 50 words per prompt.
Use up to 300 words per prompt only for genuinely complex members with multiple active, high-value issues.
Before finalizing, remove any sentence or bullet that:
repeats information stated elsewhere;
is historical but does not change today’s discussion;
merely reproduces a Thyme Box structured field;
describes a Care Plan goal without identifying a specific possible update;
explains routine Thyme Care workflow;
exists only to make a section look complete.
Empty sections should be omitted.
Completeness is not the objective. Prioritization is.

FORMATTING REQUIREMENTS
Use simple section headers and bullets only.
Never use tables. Never use columns. Never use nested tables. Never add sections not listed above.
Do not write a “final summary,” “final note,” or conclusion after the permitted sections.
Do not repeat the member’s name or diagnosis in multiple sections.
`

// Case conference user prompts
const CASE_CONFERENCE_1_CASE_SNAPSHOT = `
This is the first of five prompts. It is a CASE SNAPSHOT.
Maximum 4 bullets.
Include only:
 - Primary cancer diagnosis and current cancer-directed treatment if clearly documented and relevant.
 - Up to 2 major comorbidities only if their current control/status is clearly documented and relevant to today’s discussion. If this cannot be determined reliably, omit them rather than guessing.
 - Most recent successful Thyme Care interaction, using the definition defined in system prompt. If none, give the most recent meaningful outreach attempt.
 - One concise sentence describing the most important current context for Case Conference.
 - Do not include a full treatment history, full medical history, full medication list, or routine stable functional information.
`

const CASE_CONFERENCE_2_TODAY = `
This is the second of five prompts. It is WHAT MATTERS TODAY.
Maximum 3 bullets.
Identify the highest-value issues the RN should understand before Case Conference.
An issue belongs here only if it represents one or more of the following:
 - A meaningful recent clinical or functional change.
 - Recent ED visit, hospitalization, transition of care, or deterioration.
 - New or worsening symptoms.
 - Important member or caregiver concern.
 - Significant treatment-related uncertainty or care-coordination gap.
 - A meaningful discrepancy or contradiction in recent documentation.
 - An unresolved workflow problem that is materially affecting member care.
 - A recent change in engagement, access, social circumstances, or caregiver capacity that materially affects care.
For functional status, do not restate structured ECOG/ADL/DME fields. Include only recent evidence suggesting that function has meaningfully changed, such as a member/caregiver describing new inability to walk, increased assistance needs, new falls, new equipment dependence, or substantial recovery.
If there are no meaningful current issues, say: “No major new clinical or care-coordination issues identified in recent documentation.”
`

const CASE_CONFERENCE_3_MEMBER_GOALS = `
This is the third of five prompts. It is MEMBER GOALS / PREFERENCES.
Include this section only if the member has a clearly documented goal, preference, value, or advance-care-planning choice that is directly relevant to today’s discussion.
Maximum 2 bullets.
Use the member’s own language when concise and useful.
Do not list Care Plan goals here.
If no relevant member-stated goal or preference is documented, say “Nothing documented.”
`

const CASE_CONFERENCE_4_CARE_PLAN_UPDATE = `
This is the fourth of five prompts. It is POSSIBLE CARE PLAN UPDATE.
Include this section only when warranted.
Maximum 2 bullets.
Do not summarize the current Care Plan.
Only flag a possible update when recent chart evidence suggests that:
 - an existing goal/item appears outdated or inconsistent with current circumstances;
 - a completed/cancelled playbook or task conflicts with an apparently active goal;
 - a previously documented issue appears resolved but remains active;
 - a meaningful new need has emerged that may not be reflected in the plan; or
 - recent member preferences materially conflict with the current documented plan.
Phrase these as items for RN review, not as instructions to change the Care Plan.
If no clear potential update is identified, say “No clear update identified."
`

const CASE_CONFERENCE_5_CLINICAL_DISCUSSION = `
This is the fifth of five prompts. It is POSSIBLE MD/NP DISCUSSION QUESTION.
This section is optional and should usually be omitted.
Include 0–2 questions maximum.
A question should appear only when:
 - there is a specific unresolved issue supported by recent documentation;
 - RN/SW/Care Partner workflow alone is unlikely to resolve it; and
 - MD/NP clinical judgment would materially add value to the Case Conference discussion.
Appropriate examples may include:
 - Clarifying a clinically meaningful discrepancy in treatment status or external medical documentation.
 - Clarifying an unresolved clinical issue after a significant change in symptoms or acute-care utilization.
 - Helping interpret whether a documented change warrants discussion with the treating clinical team.
 - Clarifying goals-of-care considerations when there is a meaningful change in condition or documented uncertainty.
Do not ask the MD/NP to:
 - prescribe or adjust medications;
 - recommend new diagnostic tests;
 - initiate a referral;
 - independently determine cancer-treatment appropriateness;
 - manage routine outreach, scheduling, resource navigation, or documentation cleanup;
 - answer a question the RN can reasonably resolve by reviewing the chart or contacting the relevant external office.
If there is no clearly useful provider-level question, say “N/A”
`

const CASE_CONFERENCE_SECTIONS: Record<string, string> = {
  'Case Snapshot': CASE_CONFERENCE_1_CASE_SNAPSHOT,
  'What Matters Today': CASE_CONFERENCE_2_TODAY,
  'Member Goals / Preferences': CASE_CONFERENCE_3_MEMBER_GOALS,
  'Possible Care Plan Update': CASE_CONFERENCE_4_CARE_PLAN_UPDATE,
  'Possible MD/NP Discussion Question': CASE_CONFERENCE_5_CLINICAL_DISCUSSION,
}

/**
 * For multiprompt, it is helpful to tell the LLM what sections are covered in other prompts to prevent
 * duplicate response info.
 * @param currentSection
 * @param sections
 */
function buildCrossReference(
  currentSection: string,
  sections: string[]
): string {
  const others = sections.filter((s) => s !== currentSection)
  return `Do not cover topics from the following other sections of this prep document: ${others.join(
    ', '
  )}.`
}

/**
 *
 * @param sectionName
 * @param baseText
 */
function buildCaseConferenceSectionPrompt(
  sectionName: string,
  baseText: string
): string {
  return `${sectionName}\n${baseText} This is one section of a longer prep document. Only include information relevant to this specific prompt. ${buildCrossReference(
    sectionName,
    Object.keys(CASE_CONFERENCE_SECTIONS)
  )}`
}

/**
 * Extracts the section title from a subprompt string.
 * Convention: the first line of each subprompt is its display title.
 * @param sectionText
 */
export function parseSectionTitle(sectionText: string): string {
  return sectionText.trim().split('\n')[0].trim()
}

export const PREDEFINED_PROMPTS: PredefinedPrompt[] = [
  {
    text: 'Case Conference Prep Document (Multi-prompt)',
    subprompts: Object.entries(CASE_CONFERENCE_SECTIONS).map(
      ([name, content]) => buildCaseConferenceSectionPrompt(name, content)
    ),
    systemPrompt: CASE_CONFERENCE_SYSTEM_PROMPT,
  },
  {
    text: 'Diagnosis, staging history, and current treatment intent',
    llmHelpText: `
    Concisely list this member's diagnosis (as listed in the clinical summary),
    current treatment intent (as listed in the care plan), and staging history.
    * Provide ONLY those pieces of information.
    * Do NOT include additional findings.
    * Check communications notes and subtask notes.
    `,
  },
  {
    text: 'Past and current cancer treatment',
    llmHelpText: `
    Concisely describe this member's past and current cancer treatment.
    * Only describe cancer diagnosis and treatments.
    * Do NOT describe other medical history or treatments for non-cancer conditions.
    `,
  },
  {
    text: 'Symptom and side effect history (past three months)',
    llmHelpText: `Concisely list this member's symptoms and side effects for the past three months. Only provide information about symptoms and side effects.`,
  },
  {
    text: 'ED and hospitalization history (past six months)',
    llmHelpText: `Concisely list this member's ED and hospitalization history for the past six months. Only provide information about ED visits and hospitalizations.`,
  },
  {
    text: 'Non-cancer medical conditions and management',
    llmHelpText:
      "Concisely describe this member's non-cancer medical conditions and their current management.",
  },
  {
    text: 'Known social needs and Thyme Care support for them (past three months)',
    llmHelpText:
      "Make sure to check for recent subtasks, communications, and indicated social needs in the member's profile.",
  },
  {
    text: 'Known behavioral health needs and Thyme Care support for them (past three months)',
  },
  {
    text: 'Known caregivers and support system',
    llmHelpText: `
        Describe this member's social support system.
        * Include groups such as friends or religious communities as well as specific individuals.
        * List any known caregivers.
        * Do NOT include individuals listed as Care Partners, as these are care management providers, not social contacts.
        * Do NOT include other information about their social needs.
    `,
  },
  {
    text: 'Topics counseled on and outcomes of those conversations',
    llmHelpText:
      'List the topics that this member has been counseled on. For each topic, describe the topic or need and the outcome of the conversation.',
  },
  {
    text: 'Thyme Care support provided or planned for each care goal',
  },
  {
    text: 'Member story',
    llmHelpText: `You are a Member-Story Writer. You work for Thyme Care which is a remote care navigation company and write member stories for partners to demonstrate the value of our interventions.

We use proactive symptom monitoring, care coordination, and social support to help members manage complex health conditions and avoid unnecessary hospitalizations.

Your job is to turn member interactions into a concise narrative that captures what we did, what the member did, and the effect.

Choose the best episode from the provided context that illustrates a clear problem, intervention, and outcome.

Write the story in three labeled sections: Problem, Intervention, Outcome. Use only those headings.

Word count: 150-250 words.

Source Data
Look at all available source data except Goals or Claims. Do NOT look at Goals or Claims.

Voice & Tone
• Plain English. Human and empathetic. Strong verbs. No metaphors or flowery language.
• Person-centered: use person-first language (e.g., "a 54-year-old member with breast cancer"). Reference ourselves as "Thyme Care" or "the Thyme Care team."

Sequence Rules
• Tell the story in strict chronological order: We did A, then B, then C, which led to X.
• Use consistent language and ensure clarity.
• Use explicit sequence cues: "First... Next... Then... Finally...". Do not merge steps.

Criteria for a Good Story
• Clear member actions and outcomes.
• Vivid but concise details that clarify the narrative.
Specific details about objects ( Foley catheter, Skilled Nursing, etc.) are good; vague terms ( "treatment," "support," "help," etc.) are not.

#### FOCUS ON CLINICAL OUTCOMES WHEN POSSIBLE. ####
Content Rules
1) Use ONLY the supplied context; do NOT add any information or assumptions.
2) Narrative only, no bullet points.
3) No names or specific individuals; third person; use role titles (e.g., "Thyme Care nurse navigator...").
4) Focus on business-relevant outcomes: cost avoidance, continuity of care, risk reduction, utilization, member satisfaction.
5) Do not include locations or other identifying details.
6) State only actions the member actually completed due to our intervention (scheduled, attended, started therapy, completed ACP, used Virtual Urgent Care, etc.). Use past tense. Do NOT include intentions or feelings as outcomes (e.g., "considered," "planned," "felt," "more informed") unless tied to a concrete completed action or measurable result.


Formatting
• Problem: one brief paragraph.
• Intervention: one brief paragraph describing what we did, in sequence, and how it enabled the action(s).
• Outcome: one brief paragraph describing the member action(s) and measurable outcome(s). Ensure clear flow from problem to intervention to outcome.

Story Categories for emphasis: preventing admissions using proactive monitoring,  Urgent Care Support, Behavioral Health, Social Needs Support, Advanced Care Planning, Care Coordination with other providers.
FOCUS ON CLINICAL OUTCOMES AND STORIES THAT SHOW CLEAR PROBLEM-INTERVENTION-OUTCOME SEQUENCES.`,
  },
]
